import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Tab,
    Tabs,
    TextField,
    Grid,
    Chip,
    InputAdornment,
    Paper,
} from "@mui/material";
import { columns, Get_URI } from "./config";
import CustomDataGrid from "../../Components/CustomDataGrid";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEye from "@mui/icons-material/RemoveRedEye";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { useLang } from "../../context/LanguageContext";

const SalesList = () => {
    const [tabIndex, setTabIndex] = useState(0);
    const [isRefresh, setIsRefresh] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedSaleId, setSelectedSaleId] = useState(null);
    const [regularSales, setRegularSales] = useState([]);
    const [hiddenSales, setHiddenSales] = useState([]);
    const [searchAmount, setSearchAmount] = useState("");
    const [searchCustomer, setSearchCustomer] = useState("");
    const authHeader = useAuthHeader();
    const navigate = useNavigate();
    const { t } = useLang();

    // Tab Change Handler
    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const actions = [
        {
            title: "View",
            color: "info",
            handler: (id) => {
                navigate("/Invoice/" + id);
            },
            parameters: ["id"],
            icon: <RemoveRedEye color="info" />,
        },
        {
            title: "Edit",
            color: "primary",
            handler: (id) => {
                navigate("/EditInvoice/" + id);
            },
            parameters: ["id"],
            icon: <EditIcon color="primary" />,
        },
        {
            title: "Delete",
            color: "error",
            handler: (id) => {
                setSelectedSaleId(id);
                setOpenDeleteDialog(true);
            },
            parameters: ["id"],
            icon: <DeleteIcon />,
        },
    ];

    // Fetch Sales
    useEffect(() => {
        const getSales = async () => {
            try {
                const response = await fetch(Get_URI("GET"), {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                });
                if (response.ok) {
                    const responseData = await response.json();

                    // Filter sales by type
                    const regular = responseData.data.filter(
                        (sale) => sale.typeId === 1 || sale.typeId === 2
                    );
                    const hidden = responseData.data.filter(
                        (sale) => sale.typeId === 3
                    );

                    setRegularSales(regular);
                    setHiddenSales(hidden);
                } else {
                    throw new Error("Network response was not ok");
                }
            } catch (err) {
                toast.error("Something went wrong getting sales");
            }
        };
        getSales();
    }, [authHeader, isRefresh]);

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedSaleId(null);
    };

    const handleConfirmDelete = async () => {
        const response = await fetch(
            `${REACT_APP_BACKEND_URL}/invoices/${selectedSaleId}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: authHeader,
                },
            }
        );

        if (response.ok) {
            toast.success("Sale deleted successfully");
            setIsRefresh(!isRefresh);
            setOpenDeleteDialog(false);
            setSelectedSaleId(null);
        } else {
            toast.error("Failed to delete sale");
        }
    };

    // Filter sales based on search criteria
    const filterSales = (sales) => {
        return sales.filter((sale) => {
            const customerName = sale.OtherParty?.name?.toLowerCase() || "";
            const customerSearch = searchCustomer.toLowerCase();
            const matchesCustomer = customerName.includes(customerSearch);

            const totalAmount = sale.totalAmount?.toString() || "";
            const matchesAmount =
                searchAmount === "" || totalAmount.includes(searchAmount);

            return matchesCustomer && matchesAmount;
        });
    };

    const filteredRegularSales = filterSales(regularSales);
    const filteredHiddenSales = filterSales(hiddenSales);

    return (
        <Box sx={{ width: "100%" }}>
            {/* Modern Page Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                    color: "white",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <ReceiptLongIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {t("SALES_LIST")}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {filteredRegularSales.length + filteredHiddenSales.length} {t("SALES")}
                        </Typography>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate("/AddSale")}
                    sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "white",
                        border: "1.5px solid rgba(255,255,255,0.4)",
                        fontWeight: 700,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
                    }}
                >
                    {t("ADD_NEW_INVOICE")}
                </Button>
            </Paper>

            {/* Tabs */}
            <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab
                    label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {t("REGULAR_SALES")}
                            <Chip label={filteredRegularSales.length} size="small" color="primary" />
                        </Box>
                    }
                />
                <Tab
                    label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {t("HIDDEN_SALES")}
                            <Chip label={filteredHiddenSales.length} size="small" />
                        </Box>
                    }
                />
            </Tabs>

            {/* Search Fields */}
            <Box sx={{ mt: 1, mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t("SEARCH_BY_CUSTOMER")}
                            variant="outlined"
                            value={searchCustomer}
                            onChange={(e) => setSearchCustomer(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t("SEARCH_BY_AMOUNT")}
                            variant="outlined"
                            value={searchAmount}
                            onChange={(e) => setSearchAmount(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Tab Panels */}
            <Box sx={{ mt: 2 }}>
                {tabIndex === 0 && (
                    <CustomDataGrid
                        handleSaveConfirmClick={() => {}}
                        hasEdit={0}
                        columns={columns}
                        rows={filteredRegularSales}
                        actions={actions}
                    />
                )}
                {tabIndex === 1 && (
                    <CustomDataGrid
                        handleSaveConfirmClick={() => {}}
                        hasEdit={0}
                        columns={columns}
                        rows={filteredHiddenSales}
                        actions={actions}
                    />
                )}
            </Box>

            {/* Delete Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>{t("DELETE")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("CONFIRM")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} color="primary">
                        {t("CANCEL")}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                    >
                        {t("CONFIRM")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SalesList;
